import { Alert, Button, Card, Form, Input, Select, Space, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationSessionSelect,
  EducationStudentSelect,
} from '@/pages/education/components/EducationLookupSelects';

type ActivityRow = {
  id: string;
  code: string;
  name: string;
  session_id: string;
  is_active?: number | boolean;
  capacity?: number | null;
  active_members?: number;
};

type FormValues = {
  session_id?: string;
  activity_id: string;
  student_id: string;
  member_role: string;
  joined_on: string;
  notes?: string;
};

export default function ActivityMembershipFormPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.activity.manage') || user?.permissions?.includes('education.admin')
  );
  const [form] = Form.useForm<FormValues>();
  const sessionIdWatch = Form.useWatch('session_id', form);
  const activityIdWatch = Form.useWatch('activity_id', form);
  const presetStudent = searchParams.get('student_id') || undefined;
  const presetActivity = searchParams.get('activity_id') || undefined;

  const activities = useQuery({
    queryKey: ['education-activities', sessionIdWatch],
    enabled: canManage,
    queryFn: async () => {
      const res = await api.get<ActivityRow[]>('/education/activities', {
        params: { session_id: sessionIdWatch || undefined },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const selectedActivity = (activities.data ?? []).find((a) => a.id === activityIdWatch);

  useEffect(() => {
    form.setFieldsValue({
      student_id: presetStudent,
      activity_id: presetActivity,
      member_role: 'member',
      joined_on: new Date().toISOString().slice(0, 10),
    });
  }, [form, presetActivity, presetStudent]);

  useEffect(() => {
    if (!presetActivity || !activities.data?.length) return;
    const act = activities.data.find((a) => a.id === presetActivity);
    if (act?.session_id) form.setFieldsValue({ session_id: act.session_id, activity_id: act.id });
  }, [activities.data, form, presetActivity]);

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      api.post('/education/activity-memberships', {
        activity_id: values.activity_id,
        student_id: values.student_id,
        member_role: values.member_role,
        joined_on: values.joined_on,
        notes: values.notes,
      }),
    onSuccess: (res) => {
      const body = res.data as { status?: string; id?: string };
      if (body.status === 'waitlisted') message.success(t('activityMembershipWaitlisted'));
      else message.success(t('activityMembershipCreated'));
      queryClient.invalidateQueries({ queryKey: ['education-activity-memberships'] });
      queryClient.invalidateQueries({ queryKey: ['education-activities'] });
      const activityId = form.getFieldValue('activity_id');
      navigate(activityId ? `/education/activities/${activityId}/members` : '/education/activities/memberships');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (!canManage) {
    return (
      <EducationPageChrome title={t('enrollActivityMember')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={t('enrollActivityMember')}
      description={t('enrollActivityMemberIntro')}
      extra={<Button onClick={() => navigate('/education/activities/memberships')}>{t('backToMemberships')}</Button>}
    >
      <Card size="small" style={{ maxWidth: 640 }}>
        <Form form={form} layout="vertical" onFinish={(values) => save.mutate(values)}>
          <Form.Item name="session_id" label={t('session')}>
            <EducationSessionSelect
              style={{ width: '100%' }}
              allowClear
              onChange={() => form.setFieldsValue({ activity_id: undefined })}
            />
          </Form.Item>
          <Form.Item name="activity_id" label={t('activity')} rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={(activities.data ?? [])
                .filter((a) => !(a.is_active === 0 || a.is_active === false))
                .map((a) => ({
                  value: a.id,
                  label: `${a.code} — ${a.name}`,
                }))}
              loading={activities.isFetching}
            />
          </Form.Item>
          {selectedActivity ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('activityCapacityHintLive', {
                riders: Number(selectedActivity.active_members ?? 0),
                capacity: selectedActivity.capacity ?? '—',
              })}
            />
          ) : null}
          <Form.Item name="student_id" label={t('student')} rules={[{ required: true }]}>
            <EducationStudentSelect style={{ width: '100%' }} disabled={Boolean(presetStudent)} />
          </Form.Item>
          <Form.Item name="member_role" label={t('memberRole')} rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'member', label: t('roleMember') },
                { value: 'captain', label: t('roleCaptain') },
              ]}
            />
          </Form.Item>
          <Form.Item name="joined_on" label={t('joinedOn')} rules={[{ required: true }]}>
            <Input placeholder="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="notes" label={t('notes')}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={save.isPending}>
              {t('save')}
            </Button>
            <Button onClick={() => navigate('/education/activities/memberships')}>{t('cancel')}</Button>
          </Space>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
